import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Menu, X } from "lucide-react";
import { useI18n, LANGUAGES } from "@/lib/i18n";
import { IMAGES } from "@/lib/images";

const LINKS = [
  { key: "home", hash: "#hero" },
  { key: "services", hash: "#services" },
  { key: "fleet", hash: "#fleet" },
  { key: "experience", hash: "#experience" },
  { key: "corporate", hash: "#corporate" },
  { key: "partners", hash: "#partners" },
  { key: "destinations", hash: "#destinations" },
  { key: "about", hash: "#about" },
  { key: "contact", hash: "#contact" },
];

export const Nav = () => {
  const { t, lang, changeLang } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (hash) => {
    setMenuOpen(false);
    if (location.pathname !== "/") navigate("/" + hash);
    else document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 border-b ${scrolled || menuOpen ? "bg-[#0B0C0E]/85 backdrop-blur-xl border-white/10" : "bg-transparent border-transparent"}`}>
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img src={IMAGES.logo} alt="Europe Luxury Transfers" className="h-14 w-auto" />
        </Link>

        <nav className="hidden xl:flex items-center gap-7">
          {LINKS.map((l) => (
            <button key={l.key} data-testid={`nav-link-${l.key}`} onClick={() => go(l.hash)}
              className="text-[0.68rem] tracking-[0.22em] uppercase text-[var(--elt-ivory)]/70 hover:text-[var(--elt-gold-light)] transition-colors duration-300">
              {t(`nav.${l.key}`)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div className="relative">
            <button data-testid="language-selector-btn" onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-2 text-[0.68rem] tracking-[0.2em] uppercase text-[var(--elt-gold)] border border-[var(--elt-line)] px-3 py-2 hover:border-[var(--elt-gold)] transition-colors duration-300">
              <Globe size={13} /> {lang.toUpperCase()}
            </button>
            <AnimatePresence>
              {langOpen && (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}
                  className="absolute right-0 mt-2 w-44 bg-[#121418] border border-white/10 backdrop-blur-xl py-2">
                  {LANGUAGES.map((l) => (
                    <button key={l.code} data-testid={`lang-option-${l.code}`}
                      onClick={() => { changeLang(l.code); setLangOpen(false); }}
                      className={`w-full text-left px-4 py-2 text-sm font-light flex justify-between items-center hover:bg-white/5 transition-colors ${lang === l.code ? "text-[var(--elt-gold)]" : "text-[var(--elt-ivory)]/80"}`}>
                      <span>{l.label}</span><span className="text-[0.6rem] tracking-widest opacity-50">{l.short}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Link to="/request" data-testid="nav-request-journey-btn" className="hidden md:inline-flex btn-gold !px-6 !py-3">{t("cta.request")}</Link>

          <button data-testid="mobile-menu-btn" onClick={() => setMenuOpen(!menuOpen)} className="xl:hidden text-[var(--elt-ivory)]">
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.4 }}
            className="xl:hidden overflow-hidden bg-[#0B0C0E]/95 backdrop-blur-xl border-t border-white/10">
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button key={l.key} data-testid={`mobile-nav-link-${l.key}`} onClick={() => go(l.hash)}
                  className="text-left text-sm tracking-[0.2em] uppercase text-[var(--elt-ivory)]/80 hover:text-[var(--elt-gold)]">
                  {t(`nav.${l.key}`)}
                </button>
              ))}
              <Link to="/request" data-testid="mobile-request-journey-btn" onClick={() => setMenuOpen(false)} className="btn-gold justify-center mt-2">{t("cta.request")}</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
